import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function CreateThread() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category: "", title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.forum));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.title.trim() || !form.body.trim()) {
      toast.error("Fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/threads", form);
      toast.success("Thread posted");
      navigate(`/threads/${data.thread_id}`);
    } catch (e) {
      toast.error("Couldn't post thread");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="create-thread-page">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">New thread</div>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-6">Start something.</h1>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-md p-6 md:p-8">
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger id="category" className="mt-1.5" data-testid="select-category">
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`opt-cat-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Snook fired up in the ICW this week"
            className="mt-1.5"
            data-testid="input-thread-title"
          />
        </div>
        <div>
          <Label htmlFor="body">Body</Label>
          <Textarea
            id="body"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Tell the story."
            className="mt-1.5 min-h-[220px]"
            data-testid="input-thread-body"
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="btn-cancel-thread">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="btn-submit-thread">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Post thread
          </Button>
        </div>
      </form>
    </div>
  );
}
